import { store } from '@/store';
import {
  clearIntegrationToken,
  setIntegrationToken,
  type IntegrationToken,
} from '@/store/slices/authSlice';

/**
 * Integration Token Manager
 * Handles secure storage and management of integration partner access tokens
 */
export class IntegrationTokenManager {
  /**
   * Store integration token from authenticate response
   */
  static storeToken(
    tokenResponse: { token: string; expiresIn: string },
    partnerId: string
  ): IntegrationToken | null {
    const now = Date.now();
    const expiresInMs = parseInt(tokenResponse.expiresIn) * 1000;
    if (
      !tokenResponse.token ||
      !tokenResponse.expiresIn ||
      isNaN(expiresInMs) ||
      expiresInMs <= 0
    ) {
      console.error(
        '[IntegrationTokenManager] ❌ Invalid token or expiresIn value:',
        tokenResponse
      );
      return null;
    }
    const expiresAt = now + expiresInMs;

    const integrationToken: IntegrationToken = {
      token: tokenResponse.token,
      expiresIn: tokenResponse.expiresIn,
      expiresAt,
      partnerId,
      createdAt: now,
    };

    // Store in Redux
    store.dispatch(setIntegrationToken(integrationToken));

    return integrationToken;
  }

  /**
   * Get current integration token
   */
  static getToken(): IntegrationToken | null {
    const state = store.getState();
    return state.auth.integrationToken;
  }

  /**
   * Check if integration token is valid and not expired
   */
  static isTokenValid(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    const now = Date.now();
    const isValid = now < token.expiresAt;

    if (!isValid) {
      this.clearToken();
    }

    return isValid;
  }

  /**
   * Get valid integration token (returns null if expired or not found)
   */
  static getValidToken(): string | null {
    if (!this.isTokenValid()) {
      return null;
    }

    const token = this.getToken();
    return token?.token || null;
  }

  /**
   * Check if token is about to expire (within 5 minutes)
   */
  static isTokenExpiringSoon(minutes: number = 5): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    const now = Date.now();
    const expiresInMs = token.expiresAt - now;
    const expiresInMinutes = expiresInMs / (1000 * 60);

    return expiresInMinutes <= minutes;
  }

  /**
   * Clear integration token
   */
  static clearToken(): void {
    store.dispatch(clearIntegrationToken());
  }

  /**
   * Get token expiration info
   */
  static getTokenInfo(): {
    hasToken: boolean;
    isValid: boolean;
    expiresAt: string | null;
    expiresInMinutes: number | null;
    isExpiringSoon: boolean;
  } {
    const token = this.getToken();
    if (!token) {
      return {
        hasToken: false,
        isValid: false,
        expiresAt: null,
        expiresInMinutes: null,
        isExpiringSoon: false,
      };
    }

    const now = Date.now();
    const isValid = now < token.expiresAt;
    const expiresInMs = token.expiresAt - now;
    const expiresInMinutes = Math.max(0, expiresInMs / (1000 * 60));
    const isExpiringSoon = expiresInMinutes <= 5;

    return {
      hasToken: true,
      isValid,
      expiresAt: new Date(token.expiresAt).toISOString(),
      expiresInMinutes: Math.round(expiresInMinutes),
      isExpiringSoon,
    };
  }
}
